import Api from "../api";
import {
  GET_ALL_USER,
  SEARCH_CONTACTS,
  CONTACT_CACHE_THRESHOLD,
  USER_URL,
  ADD_USER,
  DELETE_USER,
  UPDATE_USER,
  BULK_CONTACT,
  BULK_CONTACT_URL,
  BULK_DELETE_CONTACT_URL,
  DOWNLOAD_CONTACT,
  DOWNLOAD_CONTACT_URL,
  DELETE_BULK_CONTACT,
  GET_CONTACT_GROUP_URL,
  GET_CONTACT_GROUP,
} from "../lib/constant";
import {
  isWebWorkerSupported,
  handleAPIDispatch,
  handleRejectCatch,
  addToLocalStorage,
  getFromLocalStorage,
} from "../lib/utils";
import PseudoWorker from "pseudo-worker";
import task from "../lib/task";

let worker;
if (isWebWorkerSupported()) {
  worker = new window.Worker(task);
} else {
  worker = new PseudoWorker(task);
}

const PAGE_SIZE = 200;

async function fetchContactsPage(pageToken) {
  const { data } = await Api.get(USER_URL, true, {
    page_size: PAGE_SIZE,
    page_token: pageToken,
  });
  if (!data || !data.success) {
    const err = new Error((data && data.message) || "ListContacts failed");
    err.response = { data };
    throw err;
  }
  return {
    contacts: Array.isArray(data.data) ? data.data : [],
    nextPageToken: data.nextPageToken || "",
    totalCount: typeof data.totalCount === "number" ? data.totalCount : -1,
  };
}

// Convert proto contacts + group summary into the legacy envelope that the
// web worker (src/lib/task.js) and the Sms view expect:
//   { success, data: { contacts: [...], groups: {[gid]: {id, name, contacts: []}} } }
// Each contact's `groups` is re-keyed as {groupId, name} because the worker
// reads `group.groupId` (not `group.id`).
function toLegacyEnvelope(protoContacts, groupSummary, extras = {}) {
  const groupsDict = {};
  (groupSummary || []).forEach((g) => {
    groupsDict[g.id] = { id: g.id, name: g.name, contacts: [] };
  });
  const contacts = protoContacts.map((c) => ({
    ...c,
    groups: (c.groups || []).map((g) => ({ groupId: g.id, name: g.name })),
  }));
  return {
    success: true,
    data: { contacts, groups: groupsDict, ...extras },
  };
}

export function getAllUser(smsUIReq, noAPICall, apiData) {
  // Fast path: reuse already-fetched data, just re-run the worker transform.
  if (smsUIReq && noAPICall) {
    return (dispatch) => {
      worker.postMessage({ data: apiData });
      worker.onmessage = (e) => {
        dispatch({ type: GET_ALL_USER, payload: e.data.data });
      };
    };
  }
  return (dispatch) => {
    Promise.all([
      fetchContactsPage(""),
      Api.get(GET_CONTACT_GROUP_URL).then(({ data }) => data),
    ])
      .then(async ([firstPage, summary]) => {
        const groups = (summary && summary.groups) || [];
        const tooMany =
          firstPage.totalCount >= 0 &&
          firstPage.totalCount > CONTACT_CACHE_THRESHOLD;
        if (tooMany) {
          // Large tenant — do not materialize every contact in the browser.
          // Sms view will drive the picker via searchContacts(query) instead.
          const envelope = toLegacyEnvelope(firstPage.contacts, groups, {
            largeTenantMode: true,
            totalCount: firstPage.totalCount,
          });
          dispatch({ type: GET_ALL_USER, payload: envelope });
          return;
        }
        const all = [...firstPage.contacts];
        let token = firstPage.nextPageToken;
        while (token) {
          const page = await fetchContactsPage(token);
          all.push(...page.contacts);
          token = page.nextPageToken;
        }
        const envelope = toLegacyEnvelope(all, groups, {
          largeTenantMode: false,
          totalCount: firstPage.totalCount >= 0 ? firstPage.totalCount : all.length,
        });
        if (smsUIReq) {
          worker.postMessage({ data: envelope });
          worker.onmessage = (e) => {
            dispatch({ type: GET_ALL_USER, payload: e.data.data });
          };
        } else {
          dispatch({ type: GET_ALL_USER, payload: envelope });
        }
      })
      .catch((err) => handleRejectCatch(dispatch, err, GET_ALL_USER));
  };
}

// Server-side trigram search for the Sms picker when tenant is over the
// CONTACT_CACHE_THRESHOLD. Callers should debounce (~250ms).
export function searchContacts(query) {
  return (dispatch) => {
    const q = (query || "").trim();
    // Empty query → clear previous results.
    if (!q) {
      dispatch({
        type: SEARCH_CONTACTS,
        payload: { success: true, query: "", data: { contacts: [] } },
      });
      return;
    }
    Api.get(USER_URL, true, { query: q, page_size: PAGE_SIZE })
      .then(({ data }) => {
        const contacts = Array.isArray(data && data.data) ? data.data : [];
        dispatch({
          type: SEARCH_CONTACTS,
          payload: {
            success: !!(data && data.success),
            query: q,
            data: {
              contacts: contacts.map((c) => ({
                ...c,
                groups: (c.groups || []).map((g) => ({
                  groupId: g.id,
                  name: g.name,
                })),
              })),
              totalCount:
                typeof (data && data.totalCount) === "number"
                  ? data.totalCount
                  : contacts.length,
            },
          },
        });
      })
      .catch((err) => handleRejectCatch(dispatch, err, SEARCH_CONTACTS));
  };
}

export function addUser(params) {
  const request = Api.post(USER_URL, params);
  return handleAPIDispatch(request, ADD_USER);
}

export function deleteUser(userId) {
  const url = USER_URL + "/" + userId;
  const request = Api.delete(url);
  return handleAPIDispatch(request, DELETE_USER);
}

export function updateUser(params, userId) {
  const url = USER_URL + "/" + userId;
  const request = Api.put(url, params);
  return handleAPIDispatch(request, UPDATE_USER);
}

// uploadPanel posts {contacts: [{name, mobile}, ...], groups: [gid, ...]}.
// New proto expects {contacts, groupIds}. Proto response fields sit at the top
// level ({success, successCount, failedCount, failedRows}); uploadPanel reads
// `bulkContact.data.successCount`, so renest under `data`.
export function uploadBulkContact(params) {
  const body = {
    contacts: params.contacts || [],
    groupIds: params.groups || params.groupIds || [],
  };
  return (dispatch) => {
    Api.post(BULK_CONTACT_URL, body)
      .then(({ data }) => {
        dispatch({
          type: BULK_CONTACT,
          payload: {
            success: !!(data && data.success),
            data: {
              successCount: (data && data.successCount) || 0,
              failedCount: (data && data.failedCount) || 0,
              failedRows: (data && data.failedRows) || [],
            },
          },
        });
      })
      .catch((err) => handleRejectCatch(dispatch, err, BULK_CONTACT));
  };
}

export function downloadContact() {
  const request = Api.get(DOWNLOAD_CONTACT_URL);
  return handleAPIDispatch(request, DOWNLOAD_CONTACT);
}

// Legacy callers pass {contacts: [id1, id2]}. New endpoint is POST :bulk-delete
// with {contactIds}.
export function deleteBulkContact(params) {
  const body = {
    contactIds: params.contactIds || params.contacts || [],
  };
  const request = Api.post(BULK_DELETE_CONTACT_URL, body);
  return handleAPIDispatch(request, DELETE_BULK_CONTACT);
}

// GetContactGroupSummary returns {success, groups: [{id, name, contactCount}],
// contactCount, uniqueContactCount}. Views read
// `contactGroupResponse.data.groups` (array) and `.uniqeContactCount` (sic),
// so we rewrap under `data` and keep the legacy typo. Also mirror the group
// list into localStorage so `isGroupAvaible(gId)` works on direct-URL loads.
export function getContactGroup() {
  return (dispatch) => {
    Api.get(GET_CONTACT_GROUP_URL)
      .then(({ data }) => {
        // Counts arrive as strings (proto int64 → JSON); coerce to numbers or
        // countContacts() concatenates ("1" + 0 + 0 = "100").
        const groups = ((data && data.groups) || []).map((g) => ({
          ...g,
          contactCount: Number(g.contactCount) || 0,
        }));
        const envelope = {
          success: !!(data && data.success),
          data: {
            groups,
            contactCount: Number(data && data.contactCount) || 0,
            uniqeContactCount: Number(data && data.uniqueContactCount) || 0,
          },
        };
        const stored = getFromLocalStorage() || {};
        stored.groupData = groups;
        addToLocalStorage(stored);
        dispatch({ type: GET_CONTACT_GROUP, payload: envelope });
      })
      .catch((err) => handleRejectCatch(dispatch, err, GET_CONTACT_GROUP));
  };
}
